import { useState, useEffect } from 'react';

/**
 * Countdown timer component that shows time until voting opens/closes
 * - During December: Countdown to end of year (voting closes)
 * - Outside December: Countdown to December 1st (voting opens)
 */
export function CountdownTimer({ className = "" }) {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isVotingOpen, setIsVotingOpen] = useState(false);

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const currentMonth = now.getMonth(); // 0-11, where 11 = December
      const currentYear = now.getFullYear();

      let targetDate;
      let message;

      if (currentMonth === 11) {
        // December - countdown to end of year (voting closes)
        targetDate = new Date(currentYear + 1, 0, 1, 0, 0, 0); // January 1st next year
        message = "til voting closes";
        setIsVotingOpen(true);
      } else {
        // Not December - countdown to December 1st (voting opens)
        targetDate = new Date(currentYear, 11, 1, 0, 0, 0); // December 1st this year
        message = "til voting commences";
        setIsVotingOpen(false);
        
        // If we've passed December 1st this year, target next year
        if (now > targetDate) {
          targetDate = new Date(currentYear + 1, 11, 1, 0, 0, 0);
        }
      }

      const diff = targetDate - now;

      if (diff <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0, message });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds, message });
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  if (!timeRemaining) {
    return (
      <div className={`text-center ${className}`}>
        <p className="text-sm text-[#808080]">Calculating countdown...</p>
      </div>
    );
  }

  return (
    <div className={`text-center ${className}`}>
      <div className="flex items-center justify-center gap-2 md:gap-4 flex-wrap">
        <div className="flex items-center gap-1 md:gap-2">
          <span className="text-lg md:text-2xl font-bold text-[#d0d0d0]">{timeRemaining.days}</span>
          <span className="text-xs md:text-sm text-[#808080]">days</span>
        </div>
        <span className="text-[#606060]">-</span>
        <div className="flex items-center gap-1 md:gap-2">
          <span className="text-lg md:text-2xl font-bold text-[#d0d0d0]">{timeRemaining.hours}</span>
          <span className="text-xs md:text-sm text-[#808080]">hours</span>
        </div>
        <span className="text-[#606060]">-</span>
        <div className="flex items-center gap-1 md:gap-2">
          <span className="text-lg md:text-2xl font-bold text-[#d0d0d0]">{timeRemaining.minutes}</span>
          <span className="text-xs md:text-sm text-[#808080]">minutes</span>
        </div>
        <span className="text-[#606060]">-</span>
        <div className="flex items-center gap-1 md:gap-2">
          <span className="text-lg md:text-2xl font-bold text-[#d0d0d0]">{timeRemaining.seconds}</span>
          <span className="text-xs md:text-sm text-[#808080]">seconds</span>
        </div>
        <span className="text-[#606060] ml-2">til voting {timeRemaining.message}</span>
      </div>
      {isVotingOpen && (
        <p className="text-xs md:text-sm text-[#10B981] mt-2 font-medium">
          🎉 Voting is OPEN! Votes are worth 3x this month!
        </p>
      )}
    </div>
  );
}

